import React, { useState, useMemo, useEffect, useCallback, useRef } from "react";
import i18n from "@/utils/i18n";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import Modal from "@/components/ui/modal";
import Table from "@/components/ui/table";
import Button from "@/components/ui/button";
import Chip from "@/components/ui/chip";
import Input from "@/components/ui/input";
import Dropdown from "@/components/ui/dropdown";
import DatePicker from "@/components/ui/datepicker";
import ConfirmModal from "@/components/ui/confirm-modal";
import {
  Check as CheckIcon,
  Close as CloseIcon,
  Download as DownloadIcon,
  Search as SearchIcon,
} from "@/components/icons";
import dayjs from "dayjs";
import type { TFunction } from "i18next";
import type { Education } from "../types/education-management.types";
import type {
  AttendancePresenceApi,
  AttendanceStatusApi,
} from "../types/education-management.types";
import { ExcuseReason } from "../types/education-management.types";
import type { TableColumn } from "@/types/component";
import { educationManagementApi } from "../services/education-management.api";
import { virtualClassroomsApi } from "@/features/education-catalog/services/virtual-classrooms.api";
import type { VirtualClassroomResDto } from "@/features/education-catalog/services/virtual-classrooms.api";
import styles from "./AttendanceModal.module.scss";

interface AttendanceModalProps {
  isOpen: boolean;
  onClose: () => void;
  education: Education | null;
  /** Bir sanal sınıfa kilitle; dropdown gizlenir, default tüm aramalarda bu id kullanılır. */
  lockedVirtualClassroomId?: string;
  /** Kilitli sınıfta header'da sınıf adı göstermek için (yoksa eğitim adı kullanılır). */
  lockedClassroomName?: string;
}

type AttendanceStatus = "katildi" | "katilmadi" | "girilmedi";

interface Attendee {
  id: string;
  enrollmentId: string;
  personnelId: string;
  personnelName: string;
  title: string;
  department: string;
  status: AttendanceStatus;
  /** Madde 10: status === "katilmadi" ise zorunlu. */
  excuseReason: ExcuseReason | null;
  /** Canlı sınıf giriş-çıkış özeti (yalnızca online eğitim). */
  presence: AttendancePresenceApi | null;
}

interface PendingChange {
  status: AttendanceStatus;
  excuseReason: ExcuseReason | null;
}
type PendingChanges = Map<string, PendingChange>;

function apiStatusToStatus(s: AttendanceStatusApi): AttendanceStatus {
  if (s === 1) return "katildi";
  if (s === 0) return "katilmadi";
  return "girilmedi";
}

const STATUS_CONFIG: Record<
  AttendanceStatus,
  { label: string; type: "success" | "error" | "default" }
> = {
  katildi: { label: i18n.t("attendanceLabels.katildi"), type: "success" },
  katilmadi: { label: i18n.t("attendanceLabels.katilmadi"), type: "error" },
  girilmedi: { label: "Girilmedi", type: "default" },
};

const EXCUSE_REASON_VALUES: ExcuseReason[] = [
  ExcuseReason.HEALTH,
  ExcuseReason.ANNUAL_LEAVE,
  ExcuseReason.BUSINESS_TRAVEL,
  ExcuseReason.OFFICIAL_DUTY,
  ExcuseReason.FAMILY_EMERGENCY,
  ExcuseReason.OTHER,
];

/** Bugünün yoklaması açıkken canlı sınıf sürelerinin yenilenme aralığı. */
const ATTENDANCE_AUTO_REFRESH_MS = 15_000;

const formatClock = (iso: string) => dayjs(iso).format("HH:mm");

/** "1 sa 25 dk" / "40 dk" */
const formatStayDuration = (totalSeconds: number, t: TFunction) => {
  const minutes = Math.floor(totalSeconds / 60);
  const hours = Math.floor(minutes / 60);
  const minuteLabel = t("attendanceModal.minutesShort", "dk");
  if (hours === 0) return `${minutes} ${minuteLabel}`;
  return `${hours} ${t("attendanceModal.hoursShort", "sa")} ${minutes % 60} ${minuteLabel}`;
};

const formatPresenceSessions = (presence: AttendancePresenceApi, t: TFunction) =>
  presence.sessions
    .map(
      (s) =>
        `${formatClock(s.joinedAt)}–${s.leftAt ? formatClock(s.leftAt) : t("attendanceModal.stillInside", "içeride")}`,
    )
    .join(", ");

const formatAttendanceDate = (d: Date) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const AttendanceModal: React.FC<AttendanceModalProps> = ({
  isOpen,
  onClose,
  education,
  lockedVirtualClassroomId,
  lockedClassroomName,
}) => {
  const { t } = useTranslation();
  const [attendees, setAttendees] = useState<Attendee[]>([]);
  const [selectedRows, setSelectedRows] = useState<Attendee[]>([]);
  // Pagination boyunca korunan kullanıcı düzenlemeleri (enrollmentId -> değişiklik).
  // fetchAttendance tekrar çalıştığında satırların üstüne bindirilir; save'de
  // sadece bu map'in içeriği gönderilir.
  const [pendingChanges, setPendingChanges] = useState<PendingChanges>(
    () => new Map(),
  );
  const pendingChangesRef = useRef<PendingChanges>(pendingChanges);
  useEffect(() => {
    pendingChangesRef.current = pendingChanges;
  }, [pendingChanges]);
  const attendeesRef = useRef<Attendee[]>(attendees);
  useEffect(() => {
    attendeesRef.current = attendees;
  }, [attendees]);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [attendanceDate, setAttendanceDate] = useState<Date>(() => new Date());
  const [classrooms, setClassrooms] = useState<VirtualClassroomResDto[]>([]);
  const [virtualClassroomId, setVirtualClassroomId] = useState<string>("");
  const [bulkExcuse, setBulkExcuse] = useState<ExcuseReason | null>(null);

  const getExcuseLabel = useCallback(
    (r: ExcuseReason) =>
      t(`attendanceModal.excuseReasons.${r}`, String(r)),
    [t],
  );

  const excuseReasonOptions = useMemo(
    () =>
      EXCUSE_REASON_VALUES.map((value) => ({
        label: getExcuseLabel(value),
        value: String(value),
      })),
    [getExcuseLabel],
  );

  useEffect(() => {
    if (isOpen) {
      setCurrentPage(1);
      setSearchTerm("");
      setDebouncedSearch("");
      setAttendanceDate(new Date());
      setVirtualClassroomId(lockedVirtualClassroomId ?? "");
      setBulkExcuse(null);
      setPendingChanges(new Map());
      setSelectedRows([]);
    }
  }, [isOpen, lockedVirtualClassroomId]);

  // Sanal sınıf listesini yükle (çok sınıflı eğitimde sınıf seçimi için).
  useEffect(() => {
    if (!isOpen || !education?.id) {
      setClassrooms([]);
      return;
    }
    virtualClassroomsApi
      .listByEducation(education.id)
      .then((list) => setClassrooms(list ?? []))
      .catch(() => setClassrooms([]));
  }, [isOpen, education?.id]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchTerm), 300);
    return () => clearTimeout(t);
  }, [searchTerm]);

  const fetchAttendance = useCallback(async (options?: { silent?: boolean }) => {
    if (!education?.id) return;
    // Otomatik yenilemede tablo yükleniyor durumuna geçip titremesin.
    if (!options?.silent) setIsLoading(true);
    try {
      const dateStr = formatAttendanceDate(attendanceDate);
      const { data } = await educationManagementApi.getAttendance(
        education.id,
        {
          attendanceDate: dateStr,
          page: currentPage,
          limit: pageSize,
          search: debouncedSearch.trim() || undefined,
          virtualClassroomId: virtualClassroomId || undefined,
        },
      );
      const items: Attendee[] = (data.items ?? []).map((item) => {
        const mappedStatus = apiStatusToStatus(item.status);
        const base: Attendee = {
          id: item.enrollmentId,
          enrollmentId: item.enrollmentId,
          personnelId: item.personnelId ?? "",
          personnelName: item.personnelName ?? "",
          title: item.title ?? "",
          department: item.department ?? "",
          status: mappedStatus,
          // Madde 10: yalnızca "katilmadi" için mazeret var
          excuseReason:
            mappedStatus === "katilmadi" && item.excuseReason != null
              ? (item.excuseReason as ExcuseReason)
              : null,
          presence: item.presence ?? null,
        };
        // Önceki sayfalarda yapılan değişiklikleri geri yükle.
        const pending = pendingChangesRef.current.get(item.enrollmentId);
        if (!pending) return base;
        return {
          ...base,
          status: pending.status,
          excuseReason:
            pending.status === "katilmadi" ? pending.excuseReason : null,
        };
      });
      setAttendees(items);
      setTotalRecords(data.total ?? 0);
    } catch {
      // Otomatik yenileme başarısızsa ekrandaki liste korunur.
      if (!options?.silent) {
        setAttendees([]);
        setTotalRecords(0);
      }
    } finally {
      if (!options?.silent) setIsLoading(false);
    }
  }, [education?.id, currentPage, pageSize, debouncedSearch, attendanceDate, virtualClassroomId]);

  useEffect(() => {
    if (isOpen && education?.id) {
      fetchAttendance();
    } else {
      setAttendees([]);
      setTotalRecords(0);
    }
  }, [isOpen, education?.id, fetchAttendance]);

  // Tüm sayfalardaki birikmiş değişikliklerden istatistik (confirm modal için).
  const stats = useMemo(() => {
    let attended = 0;
    let notAttended = 0;
    pendingChanges.forEach((v) => {
      if (v.status === "katildi") attended++;
      else if (v.status === "katilmadi") notAttended++;
    });
    return { attended, notAttended, total: attended + notAttended };
  }, [pendingChanges]);

  const paginatedAttendees = attendees;

  // Online eğitimde backend her satır için giriş-çıkış özeti döner.
  const hasPresence = useMemo(
    () => attendees.some((a) => a.presence != null),
    [attendees],
  );

  // Canlı ders sürerken giriş-çıkışlar ve süreler değişir: bugünün yoklaması
  // açıkken liste kendini tazeler. Bekleyen (kaydedilmemiş) seçimler korunur —
  // fetchAttendance onları gelen satırların üstüne yeniden uygular.
  const isTodaySelected =
    formatAttendanceDate(attendanceDate) === formatAttendanceDate(new Date());
  useEffect(() => {
    if (!isOpen || !education?.id || !hasPresence || !isTodaySelected) return;
    const timer = window.setInterval(() => {
      if (!document.hidden) void fetchAttendance({ silent: true });
    }, ATTENDANCE_AUTO_REFRESH_MS);
    return () => window.clearInterval(timer);
  }, [isOpen, education?.id, hasPresence, isTodaySelected, fetchAttendance]);

  /**
   * Yoklaması girilmemiş kişileri canlı sınıf süresine göre doldurur: planlanan
   * ders süresinin eşiğini geçene "Katıldı", geçmeyene "Katılmadı" (mazeret yine
   * seçilmeli). Girilmiş yoklamaya dokunmaz.
   */
  const handleFillByPresence = () => {
    const targets = attendees.filter(
      (a) => a.status === "girilmedi" && a.presence?.suggestedStatus != null,
    );
    if (targets.length === 0) {
      toast.info(
        t(
          "attendanceModal.fillByPresenceNone",
          "Bu sayfada süreye göre doldurulacak, yoklaması girilmemiş kişi yok",
        ),
      );
      return;
    }
    const statusOf = (a: Attendee): AttendanceStatus =>
      a.presence?.suggestedStatus === 1 ? "katildi" : "katilmadi";
    const targetIds = new Set(targets.map((a) => a.id));
    setAttendees((prev) =>
      prev.map((a) =>
        targetIds.has(a.id)
          ? { ...a, status: statusOf(a), excuseReason: null }
          : a,
      ),
    );
    setPendingChanges((prev) => {
      const next = new Map(prev);
      targets.forEach((a) =>
        next.set(a.enrollmentId, { status: statusOf(a), excuseReason: null }),
      );
      return next;
    });
    toast.success(
      t("attendanceModal.fillByPresenceDone", {
        count: targets.length,
        defaultValue:
          "{{count}} kişinin yoklaması canlı sınıf süresine göre dolduruldu",
      }),
    );
  };

  const handleRowSelect = (row: Attendee) => {
    setSelectedRows((prev) => {
      const exists = prev.find((r) => r.id === row.id);
      if (exists) {
        return prev.filter((r) => r.id !== row.id);
      }
      return [...prev, row];
    });
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedRows(paginatedAttendees);
    } else {
      setSelectedRows([]);
    }
  };

  const handleBulkStatusChange = (status: AttendanceStatus) => {
    if (selectedRows.length === 0) return;
    setAttendees((prev) =>
      prev.map((a) => {
        if (!selectedRows.some((s) => s.id === a.id)) return a;
        // "Katıldı"ya geçerken mazereti temizle (backend aynı kuralı zorluyor)
        return {
          ...a,
          status,
          excuseReason: status === "katilmadi" ? a.excuseReason : null,
        };
      }),
    );
    setPendingChanges((prev) => {
      const next = new Map(prev);
      selectedRows.forEach((s) => {
        const existing = next.get(s.enrollmentId);
        const excuseReason =
          status === "katilmadi"
            ? existing?.excuseReason ?? s.excuseReason ?? null
            : null;
        next.set(s.enrollmentId, { status, excuseReason });
      });
      return next;
    });
    setSelectedRows([]);
  };

  /** Seçili satırların hepsine toplu mazeret uygula (sadece status === "katilmadi" olanlar). */
  const handleApplyBulkExcuse = () => {
    if (bulkExcuse == null || selectedRows.length === 0) return;
    const selectedIds = new Set(selectedRows.map((r) => r.id));
    setAttendees((prev) =>
      prev.map((a) => {
        if (!selectedIds.has(a.id)) return a;
        if (a.status !== "katilmadi") return a;
        return { ...a, excuseReason: bulkExcuse };
      }),
    );
    setPendingChanges((prev) => {
      const next = new Map(prev);
      selectedRows.forEach((s) => {
        const existing = next.get(s.enrollmentId);
        const status = existing?.status ?? s.status;
        if (status !== "katilmadi") return;
        next.set(s.enrollmentId, { status, excuseReason: bulkExcuse });
      });
      return next;
    });
  };

  /** Tek bir satırın mazeret nedeni değiştir */
  const handleExcuseReasonChange = (id: string, value: string) => {
    const reasonNum = Number(value);
    const newExcuse =
      !Number.isNaN(reasonNum) && reasonNum >= 0 && reasonNum <= 5
        ? (reasonNum as ExcuseReason)
        : null;
    setAttendees((prev) =>
      prev.map((a) =>
        a.id === id ? { ...a, excuseReason: newExcuse } : a,
      ),
    );
    setPendingChanges((prev) => {
      const next = new Map(prev);
      const existing = next.get(id);
      const fromAttendee = attendeesRef.current.find((a) => a.id === id);
      const status = existing?.status ?? fromAttendee?.status ?? "katilmadi";
      next.set(id, { status, excuseReason: newExcuse });
      return next;
    });
  };

  /** Tüm sayfalardaki mazereti eksik (Katılmadı + mazeret yok) değişiklikler. */
  const missingExcuseAttendees = useMemo(() => {
    const out: string[] = [];
    pendingChanges.forEach((v, k) => {
      if (v.status === "katilmadi" && v.excuseReason == null) out.push(k);
    });
    return out;
  }, [pendingChanges]);

  const handleSave = () => {
    const changedCount = stats.attended + stats.notAttended;
    if (changedCount === 0) {
      toast.warning(
        t("attendance.noChanges"),
      );
      return;
    }
    if (missingExcuseAttendees.length > 0) {
      toast.error(
        t("attendanceLabels.missingExcuses", { count: missingExcuseAttendees.length }),
      );
      return;
    }
    setIsConfirmModalOpen(true);
  };

  const handleDownload = () => {
    if (attendees.length === 0) {
      toast.warning(
        t("attendance.noDataToExport"),
      );
      return;
    }
    const dateStr = formatAttendanceDate(attendanceDate);
    const headers = [
      t("attendance.personnelName"),
      t("attendance.title"),
      t("attendance.department"),
      ...(hasPresence
        ? [
            t("attendanceModal.presenceColumn", "Giriş / Çıkış"),
            t("attendanceModal.durationColumn", "İçeride Kalma"),
            "%",
          ]
        : []),
      t("attendance.status"),
      t("attendance.excuseReason"),
    ];
    const escapeCsv = (val: string) => {
      const s = String(val ?? "");
      if (/[,"\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
      return s;
    };
    const rows = attendees.map((a) => {
      const statusLabel = STATUS_CONFIG[a.status].label;
      const excuseLabel =
        a.status === "katilmadi" && a.excuseReason != null
          ? getExcuseLabel(a.excuseReason)
          : "";
      const presenceCells = hasPresence
        ? [
            a.presence ? formatPresenceSessions(a.presence, t) : "",
            a.presence ? formatStayDuration(a.presence.totalSeconds, t) : "",
            a.presence?.presencePercent != null
              ? String(a.presence.presencePercent)
              : "",
          ]
        : [];
      return [
        a.personnelName,
        a.title,
        a.department,
        ...presenceCells,
        statusLabel,
        excuseLabel,
      ]
        .map(escapeCsv)
        .join(",");
    });
    const csv = [headers.map(escapeCsv).join(","), ...rows].join("\r\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    const safeName = (education?.educationName ?? "yoklama").replace(
      /[^\w\s-]/g,
      "",
    );
    link.download = `yoklama_${safeName}_${dateStr}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    toast.success(
      t("attendance.downloadSuccess"),
    );
  };

  const handleConfirmSave = async () => {
    if (!education?.id) return;
    const dateStr = formatAttendanceDate(attendanceDate);
    // Tüm sayfalardaki birikmiş kullanıcı değişikliklerini gönder.
    const changedEntries = Array.from(pendingChanges.entries()).filter(
      ([, v]) => v.status !== "girilmedi",
    );
    const body = {
      attendanceDate: dateStr,
      attendances: changedEntries.map(([enrollmentId, v]) => {
        const attendanceStatus: 0 | 1 = v.status === "katildi" ? 1 : 0;
        // Madde 10: status === 0 (Katılmadı) ise excuseReason ZORUNLU;
        // status === 1 (Katıldı) ise hiç gönderme.
        return attendanceStatus === 0 && v.excuseReason != null
          ? {
              enrollmentId,
              attendanceStatus,
              excuseReason: v.excuseReason,
            }
          : {
              enrollmentId,
              attendanceStatus,
            };
      }),
    };
    try {
      const res = await educationManagementApi.putAttendance(
        education.id,
        body,
      );
      toast.success(
        res?.message ??
          t("attendance.saveSuccess"),
      );
      setIsConfirmModalOpen(false);
      onClose();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })
        ?.response?.data?.message;
      toast.error(
        msg ?? t("attendance.saveError"),
      );
      setIsConfirmModalOpen(false);
    }
  };

  const columns: TableColumn<Attendee>[] = [
    {
      header: t("attendance.personnelId"),
      accessor: "personnelId",
      className: "personnel-id-column",
    },
    {
      header: t("attendance.personnelName"),
      accessor: "personnelName",
      className: "personnel-name-column",
    },
    {
      header: t("attendance.title"),
      accessor: "title",
      className: "title-column",
    },
    {
      header: t("attendance.department"),
      accessor: "department",
      className: "department-column",
    },
    ...(hasPresence
      ? ([
          {
            header: t("attendanceModal.presenceColumn", "Giriş / Çıkış"),
            accessor: (row) => {
              if (!row.presence) {
                return <span className={styles.excuseMuted}>—</span>;
              }
              if (row.presence.sessions.length === 0) {
                return (
                  <span className={styles.excuseMuted}>
                    {t("attendanceModal.notJoined", "Girmedi")}
                  </span>
                );
              }
              return (
                <div className={styles.presenceSessions}>
                  {row.presence.sessions.map((s) => (
                    <span key={s.joinedAt}>
                      {formatClock(s.joinedAt)} –{" "}
                      {s.leftAt
                        ? formatClock(s.leftAt)
                        : t("attendanceModal.stillInside", "içeride")}
                    </span>
                  ))}
                </div>
              );
            },
            className: "presence-column",
          },
          {
            header: t("attendanceModal.durationColumn", "İçeride Kalma"),
            accessor: (row) => {
              const p = row.presence;
              if (!p) return <span className={styles.excuseMuted}>—</span>;
              return (
                <div className={styles.presenceDuration}>
                  <strong>{formatStayDuration(p.totalSeconds, t)}</strong>
                  {p.presencePercent != null && (
                    <span className={styles.presenceMeta}>
                      {t("attendanceModal.percentOfPlanned", {
                        percent: p.presencePercent,
                        defaultValue: "planlananın %{{percent}}'i",
                      })}
                    </span>
                  )}
                  {p.suggestedStatus != null && row.status === "girilmedi" && (
                    <span className={styles.presenceSuggestion}>
                      {t("attendanceModal.suggestion", {
                        status:
                          STATUS_CONFIG[
                            p.suggestedStatus === 1 ? "katildi" : "katilmadi"
                          ].label,
                        defaultValue: "Öneri: {{status}}",
                      })}
                    </span>
                  )}
                </div>
              );
            },
            className: "duration-column",
          },
        ] satisfies TableColumn<Attendee>[])
      : []),
    {
      header: t("attendance.status"),
      accessor: (row) => {
        const config = STATUS_CONFIG[row.status];
        return (
          <Chip type={config.type} size="small">
            {config.label}
          </Chip>
        );
      },
      className: "status-column",
    },
    {
      header: t("attendance.excuseReason"),
      accessor: (row) => {
        // Katıldı veya Girilmedi → mazeret alanı pasif / boş
        if (row.status !== "katilmadi") {
          return <span className={styles.excuseMuted}>—</span>;
        }
        const isMissing = row.excuseReason == null;
        return (
          <div
            className={styles.excuseCell}
            onClick={(e) => e.stopPropagation()}
          >
            <Dropdown
              options={excuseReasonOptions}
              value={
                row.excuseReason != null ? String(row.excuseReason) : null
              }
              selectedLabel={
                row.excuseReason != null
                  ? getExcuseLabel(row.excuseReason)
                  : undefined
              }
              placeholder={t("attendance.excuseReasonPlaceholder")}
              onSelect={(v) =>
                handleExcuseReasonChange(
                  row.id,
                  Array.isArray(v) ? v[0] : v,
                )
              }
            />
            {isMissing && (
              <span className={styles.excuseError}>
                {t("attendance.excuseRequired")}
              </span>
            )}
          </div>
        );
      },
      className: "excuse-reason-column",
    },
  ];

  const customHeader = (
    <div className={styles.modalHeader}>
      <div className={styles.headerLeft}>
        <h3 className={styles.headerTitle}>
          {t("attendance.title")}
        </h3>
        <span className={styles.headerBadge}>
          {lockedClassroomName?.trim() || education?.educationName}
        </span>
      </div>
      <button className={styles.closeButton} onClick={onClose}>
        <CloseIcon width={20} height={20} color="#34314A" />
      </button>
    </div>
  );

  const customFooter = (
    <div className={styles.modalFooter}>
      <Button
        variant="contained"
        color="primary"
        size="large"
        onClick={handleSave}
        className={styles.saveButton}
      >
        {t("attendance.save")}
      </Button>
    </div>
  );

  const confirmMessage = (
    <>
      Seçilen <span className="highlight-success">{stats.attended} adet</span>{" "}
      kişinin katıldığını,{" "}
      <span className="highlight-error">{stats.notAttended} adet</span> kişinin
      katılmadığını onayladığınızda personeller için devamsızlık girilmiş olur.
    </>
  );

  if (!education) return null;

  return (
    <>
      <Modal
        title={t("attendance.title")}
        isOpen={isOpen}
        onClose={onClose}
        size="fullscreen"
        customHeader={customHeader}
        customFooter={customFooter}
      >
        <div className={styles.modalContent}>
          {/* Toolbar */}
          <div className={styles.toolbar}>
            <div className={styles.toolbarLeft}>
              <div className={styles.toolbarField}>
                <DatePicker
                  label={t("attendanceModal.dateLabel")}
                  value={attendanceDate}
                  onChange={(v) => {
                    const next = Array.isArray(v) ? v[0] : v;
                    if (next instanceof Date) {
                      setAttendanceDate(next);
                      setCurrentPage(1);
                      // Farklı tarih = farklı yoklama bağlamı; birikmiş değişiklikleri sıfırla.
                      setPendingChanges(new Map());
                      setSelectedRows([]);
                    }
                  }}
                />
              </div>
              {!lockedVirtualClassroomId && classrooms.length > 1 && (
                <div className={styles.toolbarField}>
                  <Dropdown
                    label={t("attendanceModal.classroomLabel")}
                    options={[
                      {
                        label: t("attendanceModal.classroomAll"),
                        value: "",
                      },
                      ...classrooms.map((c, idx) => ({
                        label:
                          c.name?.trim() ||
                          `${t("attendanceModal.classroomLabel")} ${idx + 1}`,
                        value: c.id,
                      })),
                    ]}
                    value={virtualClassroomId}
                    onSelect={(v) => {
                      const next = Array.isArray(v) ? v[0] : v;
                      setVirtualClassroomId(next ?? "");
                      setCurrentPage(1);
                      // Farklı sınıf = farklı katılımcı kümesi; birikmiş değişiklikleri sıfırla.
                      setPendingChanges(new Map());
                      setSelectedRows([]);
                    }}
                  />
                </div>
              )}
              <div className={styles.searchWrapper}>
                <Input
                  type="text"
                  placeholder={t("attendance.search")}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  startIcon={<SearchIcon width={20} height={20} />}
                />
              </div>
              <button
                type="button"
                className={styles.downloadButton}
                onClick={handleDownload}
                title={t("attendance.download")}
              >
                <DownloadIcon width={20} height={20} />
              </button>
            </div>
            <div className={styles.toolbarRight}>
              <div className={styles.bulkExcuseWrap}>
                <Dropdown
                  options={excuseReasonOptions}
                  value={bulkExcuse != null ? String(bulkExcuse) : null}
                  selectedLabel={
                    bulkExcuse != null ? getExcuseLabel(bulkExcuse) : undefined
                  }
                  placeholder={t("attendanceModal.bulkExcuseLabel")}
                  onSelect={(v) => {
                    const raw = Array.isArray(v) ? v[0] : v;
                    const num = Number(raw);
                    setBulkExcuse(
                      !Number.isNaN(num) && num >= 0 && num <= 5
                        ? (num as ExcuseReason)
                        : null,
                    );
                  }}
                />
                <Button
                  variant="outlined"
                  color="default"
                  size="medium"
                  onClick={handleApplyBulkExcuse}
                  disabled={bulkExcuse == null || selectedRows.length === 0}
                >
                  {t("attendanceModal.bulkExcuseApply")}
                </Button>
              </div>
              {hasPresence && (
                <Button
                  variant="outlined"
                  color="primary"
                  size="medium"
                  onClick={handleFillByPresence}
                >
                  {t("attendanceModal.fillByPresence", "Süreye göre doldur")}
                </Button>
              )}
              <Button
                variant="outlined"
                color="success"
                size="medium"
                onClick={() => handleBulkStatusChange("katildi")}
                disabled={selectedRows.length === 0}
                startIcon={<CheckIcon width={16} height={16} />}
              >
                {t("attendance.attended")}
              </Button>
              <Button
                variant="outlined"
                color="error"
                size="medium"
                onClick={() => handleBulkStatusChange("katilmadi")}
                disabled={selectedRows.length === 0}
                startIcon={<CloseIcon width={16} height={16} />}
              >
                {t("attendance.notAttended")}
              </Button>
            </div>
          </div>

          {hasPresence && (
            <p className={styles.presenceHint}>
              {t(
                "attendanceModal.presenceHint",
                "Süreler canlı sınıftaki giriş-çıkışlardan hesaplanır.",
              )}
            </p>
          )}

          {/* Table */}
          <div className={styles.tableContainer}>
            <Table
              columns={columns}
              data={paginatedAttendees}
              selectable
              selectedRows={selectedRows}
              onRowSelect={handleRowSelect}
              onSelectAll={handleSelectAll}
              getRowId={(row) => row.id}
              currentPage={currentPage}
              pageSize={pageSize}
              totalRecords={totalRecords}
              onPageChange={(p) => {
                setCurrentPage(p);
                // Seçim her sayfanın görünür satırlarına özeldir.
                setSelectedRows([]);
              }}
              onPageSizeChange={(s) => {
                setPageSize(s);
                setCurrentPage(1);
                setSelectedRows([]);
              }}
              isLoading={isLoading}
              emptyMessage={t("attendance.noData")}
            />
          </div>
        </div>
      </Modal>

      {/* Confirm Modal */}
      <ConfirmModal
        isOpen={isConfirmModalOpen}
        onClose={() => setIsConfirmModalOpen(false)}
        onConfirm={handleConfirmSave}
        title={t("attendance.confirmTitle")}
        message={confirmMessage}
        confirmText={t("attendance.confirm")}
        cancelText={t("attendance.cancel")}
      />
    </>
  );
};

export default AttendanceModal;
